import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listEmailThreadsV2 } from 'services/mailbox/mailboxServiceV2';

const QUERY_KEY = 'emailThreadsV2';
const DEFAULT_LIMIT = 20;

/**
 * Hook for fetching paginated email threads from the V2 RDS endpoint.
 * Uses offset-based pagination: limit + offset instead of cursor tokens.
 *
 * @param {object} params
 * @param {string} [params.searchKeyword] - Search by subject/content
 * @param {boolean} [params.isSent=false] - true = sent, false = inbox
 * @param {boolean} [params.isUnread=false] - true = fetch only unread threads (server-side)
 * @param {object} [params.filters] - Filter options
 * @param {string} [params.filters.caseId]
 * @param {string} [params.filters.mailDateLower]
 * @param {string} [params.filters.mailDateUpper]
 * @param {string[]} [params.filters.mappingStatuses]
 * @param {string[]} [params.filters.userFilter]
 * @param {number} [params.limit=20] - Page size
 * @param {boolean} [params.isEmailLog=false]
 */
const useEmailThreadsV2 = ({
  searchKeyword = '',
  isSent = false,
  isUnread = false,
  filters = {},
  limit = DEFAULT_LIMIT,
  isEmailLog = false,
} = {}) => {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [threads, setThreads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);

  const queryParams = {
    caseId: filters.caseId,
    isSent,
    isUnread: isUnread || undefined,
    mailDateLower: filters.mailDateLower || filters.mail_date_lower,
    mailDateUpper: filters.mailDateUpper || filters.mail_date_upper,
    searchKeyword,
    mappingStatus: filters.mappingStatuses?.join(','),
    filterByUser: filters.userFilter?.join(','),
    limit,
    offset,
    isEmailLog,
  };

  const { data, isLoading, isFetching, refetch } = useQuery([QUERY_KEY, queryParams], () => listEmailThreadsV2(queryParams), {
    keepPreviousData: true,
    staleTime: 30_000,
    onSuccess: (res) => {
      const incoming = res?.data?.email_threads ?? [];
      const pagination = res?.data?.pagination ?? {};

      if (offset === 0) {
        setThreads(incoming.map((t, i) => ({ ...t, index: i })));
      } else {
        setThreads((prev) => [...prev, ...incoming.map((t, i) => ({ ...t, index: prev.length + i }))]);
      }

      setTotalCount(pagination.total_count ?? 0);
      setHasMore(pagination.has_more ?? false);
      setPaginationLoading(false);
    },
    onError: (err) => {
      console.error('[useEmailThreadsV2] fetch error:', err);
      setPaginationLoading(false);
    },
  });

  // Reset offset & threads whenever filters/keyword/tab changes
  useEffect(() => {
    setOffset(0);
    setThreads([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchKeyword,
    isSent,
    isUnread,
    filters.caseId,
    filters.mailDateLower,
    filters.mailDateUpper,
    filters.mail_date_lower,
    filters.mail_date_upper,
    JSON.stringify(filters.mappingStatuses),
    JSON.stringify(filters.userFilter),
    isEmailLog,
  ]);

  const handleLoadMore = useCallback(() => {
    if (paginationLoading || isLoading || !hasMore) return;
    setPaginationLoading(true);
    setOffset((prev) => prev + limit);
  }, [paginationLoading, isLoading, hasMore, limit]);

  const refresh = useCallback(() => {
    setOffset(0);
    setThreads([]);
    queryClient.invalidateQueries([QUERY_KEY]);
  }, [queryClient]);

  return {
    threads,
    loading: isLoading && offset === 0,
    paginationLoading: paginationLoading || (isFetching && offset > 0),
    hasMore,
    totalCount,
    offset,
    handleLoadMore,
    refresh,
    refetch,
  };
};

export default useEmailThreadsV2;
